/**
 * Users (Owner/Admin, user:manage) — the portal-STAFF roster, in two tabs:
 *  • "Portal users" — staff who have SIGNED IN (provisioned app_users): change role / enable-disable /
 *    DELETE. Sensitive writes are guarded server-side (last-owner, self-lockout, only-an-Owner-mints-an-
 *    Owner, disable-before-delete) and audited; the server's 409/403 message is surfaced verbatim.
 *  • "Zoom roster" — staff synced from Zoom who HAVEN'T signed in yet, plus anyone an admin has REVOKED.
 *    Each can be INVITED (an email with a sign-in link); once they sign in they move to "Portal users".
 *    "Sync users now" pulls the roster on demand — it also refreshes itself daily at 02:30 BKK.
 * This is distinct from a customer's Zoom users (that lives in Customer-360).
 *
 * THE LIFECYCLE, in the principal's words: "Deleting removes them from the Portal user list; they go
 * back to being just a Zoom-roster entry. If you want them back, you press Invite again." Delete is
 * therefore a round trip, not a dead end — a deleted user reappears HERE, on the Zoom-roster tab, badged
 * `revoked`, and an Invite (the only thing that clears the server-side tombstone) brings them back.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiRequestError } from "@/api/client";
import { PermissionGate } from "@/components/portal/PermissionGate";
import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  Select,
  SkeletonRows,
  StatusPill,
  Table,
  TabPanel,
  Tabs,
  useNotifier,
  type Column,
  type StatusTone,
} from "@/components/ui";
import { useRbac } from "@/context/RbacContext";
import { DeleteUserModal } from "@/features/users/DeleteUserModal";
import { RosterSyncControl } from "@/features/users/RosterSyncControl";
import { deleteRefusal } from "@/features/users/deleteGuards";
import { formatInstant } from "@/lib/format";
import type { AdminUser, Permission, Role } from "@/api/types";

// Exhaustive BY CONSTRUCTION: keyed on AdminUser["status"], so the day the API grows a status, this map
// fails `tsc` instead of rendering a tone-less pill. (`revoked` arrived exactly that way.)
const STATUS_TONE: Record<AdminUser["status"], StatusTone> = {
  active: "ok",
  disabled: "danger",
  invited: "info",
  not_invited: "neutral",
  off_roster: "warn",
  // Deliberately as loud as `disabled`: an admin threw them out. It is not the quiet "nobody has got
  // round to inviting them" grey, and the difference is the whole point of the status existing.
  revoked: "danger",
};

const ROLE_OPTIONS: Role[] = ["owner", "admin", "viewer", "custom"];
/** Permissions an admin may toggle for a Custom role (search:read is always granted server-side). */
const GRANTABLE: Permission[] = ["customer:read", "money:read", "worklist:read", "audit:read"];

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

/** Client-side filter (list is already fully loaded) on name + email, case-insensitive. */
function filterUsers(users: AdminUser[], q: string): AdminUser[] {
  const term = q.trim().toLowerCase();
  if (!term) return users;
  return users.filter(
    (u) =>
      (u.email?.toLowerCase().includes(term) ?? false) ||
      (u.display_name?.toLowerCase().includes(term) ?? false),
  );
}

function UserSearch({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative w-full max-w-xs">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--text-subtle)"
        aria-hidden
      />
      <Input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        autoComplete="off"
        spellCheck={false}
        className="h-9 w-full pl-9"
      />
    </div>
  );
}

export function UsersPage() {
  const { t } = useTranslation();
  return (
    <PermissionGate
      permission="user:manage"
      fallback={<ErrorState title={t("state.forbiddenTitle")} body={t("state.forbiddenBody")} />}
    >
      <UsersBody />
    </PermissionGate>
  );
}

function UsersBody() {
  const { t } = useTranslation();
  const { me } = useRbac();
  const qc = useQueryClient();
  const { notify } = useNotifier();
  const query = useQuery({
    queryKey: ["adminUsers"],
    queryFn: ({ signal }) => api.adminUsers(signal),
  });

  const [tab, setTab] = useState<"portal" | "roster">("portal");
  const [roleTarget, setRoleTarget] = useState<AdminUser | null>(null);
  const [activeTarget, setActiveTarget] = useState<AdminUser | null>(null);
  const [inviteTarget, setInviteTarget] = useState<AdminUser | null>(null);
  const [cancelTarget, setCancelTarget] = useState<AdminUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  /** The server's verbatim refusal if the DELETE loses a race with the list snapshot — rendered INSIDE
   *  the open modal, not only as a toast that scrolls away. */
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [portalQuery, setPortalQuery] = useState("");
  const [rosterQuery, setRosterQuery] = useState("");

  const refresh = () => qc.invalidateQueries({ queryKey: ["adminUsers"] });
  const onErr = (e: unknown) =>
    notify({ tone: "danger", title: t("adminUsers.errorTitle"), body: errMessage(e, "") });

  function closeDelete() {
    setDeleteTarget(null);
    setDeleteError(null);
  }

  const roleMut = useMutation({
    mutationFn: (v: { id: string; role: Role; custom?: Permission[] }) =>
      api.setUserRole(v.id, {
        role: v.role,
        ...(v.role === "custom" ? { custom_permissions: v.custom ?? [] } : {}),
      }),
    onSuccess: () => {
      refresh();
      setRoleTarget(null);
      notify({ tone: "ok", title: t("adminUsers.roleUpdated") });
    },
    onError: onErr,
  });

  const activeMut = useMutation({
    mutationFn: (v: { id: string; is_active: boolean }) =>
      api.setUserActive(v.id, { is_active: v.is_active }),
    onSuccess: () => {
      refresh();
      setActiveTarget(null);
      notify({ tone: "ok", title: t("adminUsers.accessUpdated") });
    },
    onError: onErr,
  });

  const inviteMut = useMutation({
    mutationFn: (email: string) => api.inviteUser(email),
    onSuccess: (res) => {
      refresh();
      setInviteTarget(null);
      notify(
        res.email_sent
          ? {
              tone: "ok",
              title: t("adminUsers.inviteSent", { email: res.email }),
              // An invite is the ONLY thing that lifts a revocation. When it does, say so — the operator
              // has just reversed another admin's deliberate decision, and that deserves a sentence.
              ...(res.revoked_cleared ? { body: t("adminUsers.inviteRestored") } : {}),
            }
          : {
              tone: "warn",
              title: t("adminUsers.inviteRecorded"),
              body: t("adminUsers.inviteEmailFailed"),
            },
      );
    },
    onError: onErr,
  });

  const deleteMut = useMutation({
    mutationFn: (userId: string) => api.deleteUser(userId),
    onSuccess: (res) => {
      refresh();
      closeDelete();
      notify({
        tone: "ok",
        title: t("adminUsers.deleted", { email: res.email }),
        body: t("adminUsers.deletedBody"),
      });
    },
    // The pre-flight in deleteGuards is a snapshot; the server holds the row FOR UPDATE and is the gate.
    // When it refuses, show ITS words, in the modal that is still open — the operator is standing right
    // there, and a refusal they have to chase into a toast is a refusal they will misread as a bug.
    onError: (e: unknown) => {
      setDeleteError(errMessage(e, t("adminUsers.errorTitle")));
      onErr(e);
    },
  });

  const cancelMut = useMutation({
    mutationFn: (email: string) => api.cancelInvite(email),
    onSuccess: () => {
      refresh();
      setCancelTarget(null);
      notify({ tone: "ok", title: t("adminUsers.inviteCancelled") });
    },
    onError: onErr,
  });

  const portalUsers = (query.data?.users ?? []).filter((u) => u.provisioned);
  const rosterPending = (query.data?.users ?? []).filter((u) => !u.provisioned);
  const filteredPortal = filterUsers(portalUsers, portalQuery);
  const filteredRoster = filterUsers(rosterPending, rosterQuery);

  const portalColumns: Column<AdminUser>[] = [
    {
      key: "user",
      header: t("adminUsers.colUser"),
      sortValue: (r) => r.email,
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span className="font-medium text-(--text)">{r.display_name ?? r.email}</span>
          {r.display_name && (
            <span className="text-(length:--text-2xs) text-(--text-muted)">{r.email}</span>
          )}
        </span>
      ),
    },
    {
      key: "role",
      header: t("adminUsers.colRole"),
      sortValue: (r) => r.role ?? "",
      cell: (r) => (r.role ? t(`role.${r.role}`) : "—"),
    },
    {
      key: "status",
      header: t("adminUsers.colStatus"),
      sortValue: (r) => r.status,
      cell: (r) => (
        <StatusPill tone={STATUS_TONE[r.status]} className="cursor-default">
          {t(`adminUsers.status.${r.status}`)}
        </StatusPill>
      ),
    },
    {
      key: "last_login",
      header: t("adminUsers.colLastLogin"),
      numeric: true,
      sortValue: (r) => r.last_login_at ?? "",
      cell: (r) => (r.last_login_at ? formatInstant(r.last_login_at) : t("adminUsers.never")),
    },
    {
      key: "last_seen",
      header: t("adminUsers.colLastSeen"),
      numeric: true,
      sortValue: (r) => r.last_seen_at ?? "",
      cell: (r) => (r.last_seen_at ? formatInstant(r.last_seen_at) : t("adminUsers.never")),
    },
    {
      key: "actions",
      header: t("adminUsers.colActions"),
      align: "right",
      cell: (r) => {
        const isSelf = r.user_id != null && r.user_id === me.id;
        // Why a delete WOULD be refused, if it would. Drives the dimming — and, more importantly, the
        // sentence the modal shows. Never used to *hide* or hard-`disabled` the control: an operator who
        // cannot find out why is an operator who files a bug against the portal.
        const refusal = deleteRefusal(r, me, portalUsers);
        return (
          <span className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={isSelf}
              onClick={() => setRoleTarget(r)}
            >
              {t("adminUsers.changeRole")}
            </Button>
            <Button
              size="sm"
              variant={r.is_active ? "ghost" : "secondary"}
              disabled={isSelf}
              onClick={() => setActiveTarget(r)}
            >
              {r.is_active ? t("adminUsers.disable") : t("adminUsers.enable")}
            </Button>
            {/* aria-disabled, NOT disabled: it reads as unavailable to a screen reader and looks
                unavailable on screen, but it stays focusable and clickable — and the click is what
                delivers the reason. A truly `disabled` button eats its own pointer events, so even a
                tooltip on it is unreliable. The destructive act itself is never offered here; it lives
                behind the modal, which only shows it when the delete is actually allowed. */}
            <Button
              size="sm"
              variant="ghost"
              aria-disabled={refusal !== null || undefined}
              className={refusal ? "opacity-50" : "text-(--danger)"}
              onClick={() => {
                setDeleteError(null);
                setDeleteTarget(r);
              }}
            >
              {t("adminUsers.delete")}
            </Button>
          </span>
        );
      },
    },
  ];

  const rosterColumns: Column<AdminUser>[] = [
    {
      key: "user",
      header: t("adminUsers.colUser"),
      sortValue: (r) => r.email,
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span className="font-medium text-(--text)">{r.display_name ?? r.email}</span>
          {r.display_name && (
            <span className="text-(length:--text-2xs) text-(--text-muted)">{r.email}</span>
          )}
        </span>
      ),
    },
    {
      key: "invited",
      header: t("adminUsers.colInvited"),
      // Revoked sorts to the top of the column: it is the row that wants a decision.
      sortValue: (r) => (r.status === "revoked" ? "zzz" : (r.invited_at ?? "")),
      cell: (r) => {
        // A REVOKED person carries no invited_at (Delete clears it, precisely so this cell cannot go on
        // claiming "Invited 3 days ago" about an account that no longer exists). Without this branch
        // they would fall through to "Not invited" — which is true of the invite stamp and a lie about
        // the person: it would render an admin's deliberate ejection as an oversight nobody got to.
        if (r.status === "revoked") {
          return (
            <span className="flex flex-col items-start gap-0.5 leading-tight">
              <StatusPill tone={STATUS_TONE.revoked} className="cursor-default">
                {t("adminUsers.status.revoked")}
              </StatusPill>
              <span className="text-(length:--text-2xs) text-(--text-muted)">
                {r.revoked_at
                  ? t("adminUsers.revokedAt", {
                      time: formatInstant(r.revoked_at),
                      email: r.revoked_by_email ?? "—",
                    })
                  : t("adminUsers.statusHelp.revoked")}
              </span>
            </span>
          );
        }
        return r.invited_at ? (
          <StatusPill tone="info" className="cursor-default">
            {t("adminUsers.invitedAt", { time: formatInstant(r.invited_at) })}
          </StatusPill>
        ) : (
          <span className="text-(--text-subtle)">{t("adminUsers.notInvited")}</span>
        );
      },
    },
    {
      key: "actions",
      header: t("adminUsers.colActions"),
      align: "right",
      cell: (r) => (
        <span className="flex justify-end gap-2">
          <Button
            size="sm"
            variant={r.invited_at ? "secondary" : "primary"}
            onClick={() => setInviteTarget(r)}
          >
            {/* For a revoked person the button is not a fresh invite — it is the one act that lets
                someone we ejected back in. Label it as what it does. */}
            {r.status === "revoked"
              ? t("adminUsers.inviteBack")
              : r.invited_at
                ? t("adminUsers.reinvite")
                : t("adminUsers.invite")}
          </Button>
          {r.invited_at && (
            <Button size="sm" variant="ghost" onClick={() => setCancelTarget(r)}>
              {t("adminUsers.cancelInvite")}
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <section aria-labelledby="users-title" className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <h1
          id="users-title"
          className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
        >
          {t("adminUsers.title")}
        </h1>
        <p className="text-(length:--text-sm) text-(--text-muted)">{t("adminUsers.subtitle")}</p>
      </header>

      <Tabs
        items={[
          { value: "portal", label: t("adminUsers.tabPortal") },
          { value: "roster", label: t("adminUsers.tabRoster") },
        ]}
        value={tab}
        onValueChange={(v) => setTab(v as "portal" | "roster")}
        label={t("adminUsers.title")}
      />

      {query.isError ? (
        <ErrorState title={t("state.errorTitle")} body={t("state.errorBody")} />
      ) : query.isPending ? (
        <SkeletonRows rows={5} />
      ) : (
        <>
          <TabPanel
            id="users"
            value="portal"
            active={tab === "portal"}
            className="flex flex-col gap-3"
          >
            <p className="text-(length:--text-xs) text-(--text-muted)">
              {t("adminUsers.portalHint")}
            </p>
            <UserSearch
              value={portalQuery}
              onChange={setPortalQuery}
              placeholder={t("adminUsers.searchPlaceholder")}
            />
            <Table
              columns={portalColumns}
              rows={filteredPortal}
              rowKey={(r) => r.user_id ?? r.email}
              caption={t("adminUsers.tabPortal")}
              initialSort={{ key: "user", dir: "asc" }}
              empty={
                portalQuery.trim() ? (
                  <EmptyState
                    title={t("search.emptyTitle")}
                    body={t("search.emptyBody", { term: portalQuery.trim() })}
                  />
                ) : (
                  <EmptyState title={t("adminUsers.empty")} body={t("adminUsers.emptyBody")} />
                )
              }
            />
          </TabPanel>

          <TabPanel
            id="users"
            value="roster"
            active={tab === "roster"}
            className="flex flex-col gap-3"
          >
            <p className="text-(length:--text-xs) text-(--text-muted)">
              {t("adminUsers.rosterHint")}
            </p>
            <RosterSyncControl />
            <UserSearch
              value={rosterQuery}
              onChange={setRosterQuery}
              placeholder={t("adminUsers.searchPlaceholder")}
            />
            <Table
              columns={rosterColumns}
              rows={filteredRoster}
              rowKey={(r) => r.email}
              caption={t("adminUsers.tabRoster")}
              initialSort={{ key: "user", dir: "asc" }}
              empty={
                rosterQuery.trim() ? (
                  <EmptyState
                    title={t("search.emptyTitle")}
                    body={t("search.emptyBody", { term: rosterQuery.trim() })}
                  />
                ) : (
                  <EmptyState
                    title={t("adminUsers.rosterEmpty")}
                    body={t("adminUsers.rosterEmptyBody")}
                  />
                )
              }
            />
          </TabPanel>
        </>
      )}

      {roleTarget && (
        <RoleModal
          target={roleTarget}
          pending={roleMut.isPending}
          onClose={() => setRoleTarget(null)}
          onSubmit={(role, custom) =>
            roleMut.mutate({ id: roleTarget.user_id as string, role, custom })
          }
        />
      )}

      {activeTarget && (
        <Modal
          open
          onClose={() => setActiveTarget(null)}
          title={
            activeTarget.is_active
              ? t("adminUsers.disableDialogTitle")
              : t("adminUsers.enableDialogTitle")
          }
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setActiveTarget(null)}>
                {t("action.cancel")}
              </Button>
              <Button
                variant={activeTarget.is_active ? "danger" : "primary"}
                loading={activeMut.isPending}
                onClick={() =>
                  activeMut.mutate({
                    id: activeTarget.user_id as string,
                    is_active: !activeTarget.is_active,
                  })
                }
              >
                {activeTarget.is_active ? t("adminUsers.disable") : t("adminUsers.enable")}
              </Button>
            </>
          }
        >
          <p className="text-(length:--text-sm) text-(--text-muted)">
            {t(
              activeTarget.is_active
                ? "adminUsers.disableDialogBody"
                : "adminUsers.enableDialogBody",
              { email: activeTarget.email },
            )}
          </p>
        </Modal>
      )}

      {deleteTarget && (
        <DeleteUserModal
          target={deleteTarget}
          actor={me}
          users={portalUsers}
          pending={deleteMut.isPending}
          serverError={deleteError}
          onClose={closeDelete}
          onConfirm={(id) => deleteMut.mutate(id)}
        />
      )}

      {inviteTarget && (
        <Modal
          open
          onClose={() => setInviteTarget(null)}
          title={t("adminUsers.inviteDialogTitle")}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setInviteTarget(null)}>
                {t("action.cancel")}
              </Button>
              <Button
                loading={inviteMut.isPending}
                onClick={() => inviteMut.mutate(inviteTarget.email)}
              >
                {t("adminUsers.sendInvite")}
              </Button>
            </>
          }
        >
          <p className="text-(length:--text-sm) text-(--text-muted)">
            {t("adminUsers.inviteDialogBody", { email: inviteTarget.email })}
          </p>
        </Modal>
      )}

      {cancelTarget && (
        <Modal
          open
          onClose={() => setCancelTarget(null)}
          title={t("adminUsers.cancelInviteDialogTitle")}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setCancelTarget(null)}>
                {t("adminUsers.keepInvite")}
              </Button>
              <Button
                variant="danger"
                loading={cancelMut.isPending}
                onClick={() => cancelMut.mutate(cancelTarget.email)}
              >
                {t("adminUsers.cancelInvite")}
              </Button>
            </>
          }
        >
          <p className="text-(length:--text-sm) text-(--text-muted)">
            {t("adminUsers.cancelInviteDialogBody", { email: cancelTarget.email })}
          </p>
        </Modal>
      )}
    </section>
  );
}

function RoleModal({
  target,
  pending,
  onClose,
  onSubmit,
}: {
  target: AdminUser;
  pending: boolean;
  onClose: () => void;
  onSubmit: (role: Role, custom: Permission[]) => void;
}) {
  const { t } = useTranslation();
  const [role, setRole] = useState<Role>(target.role ?? "viewer");
  // Seed the toggles from the user's CURRENT grants when they're already Custom (so Save is an explicit
  // edit, not a silent reset); a fresh Custom assignment starts from a sensible default. search:read is
  // always granted server-side, so it isn't a toggle here.
  const [custom, setCustom] = useState<Set<Permission>>(() =>
    target.role === "custom"
      ? new Set<Permission>(target.custom_permissions.filter((p) => GRANTABLE.includes(p)))
      : new Set<Permission>(["customer:read"]),
  );

  function toggle(p: Permission) {
    setCustom((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`${t("adminUsers.roleDialogTitle")} — ${target.email}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button loading={pending} onClick={() => onSubmit(role, [...custom])}>
            {t("adminUsers.save")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-(length:--text-sm) text-(--text-muted)">
          {t("adminUsers.roleDialogBody")}
        </p>
        <label className="flex flex-col gap-1.5">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("adminUsers.roleLabel")}
          </span>
          <Select
            value={role}
            onChange={(v) => setRole(v as Role)}
            options={ROLE_OPTIONS.map((r) => ({ value: r, label: t(`role.${r}`) }))}
            aria-label={t("adminUsers.roleLabel")}
          />
        </label>

        {role === "custom" && (
          <fieldset className="flex flex-col gap-2 rounded-(--radius) border border-(--border) p-3">
            <legend className="px-1 text-(length:--text-xs) font-medium text-(--text-muted)">
              {t("adminUsers.customPermsLabel")}
            </legend>
            <p className="text-(length:--text-2xs) text-(--text-subtle)">
              {t("adminUsers.customPermsNote")}
            </p>
            {GRANTABLE.map((p) => (
              <label
                key={p}
                className="flex items-center gap-2 text-(length:--text-sm) text-(--text)"
              >
                <input
                  type="checkbox"
                  checked={custom.has(p)}
                  onChange={() => toggle(p)}
                  className="size-4 accent-(--brand)"
                />
                {t(`adminUsers.permLabel.${p}`)}
              </label>
            ))}
          </fieldset>
        )}
      </div>
    </Modal>
  );
}
